import { useIonRouter } from '@ionic/react';
import {
  ClockCounterClockwiseIcon,
  ForkKnifeIcon,
  IconProps,
  IdentificationCardIcon,
  PhonePlusIcon,
  PlusIcon,
  ShieldCheckIcon,
} from '@phosphor-icons/react';
import React, { useCallback, useMemo, useState } from 'react';

import AlimentacionPesaje from '../../components/Alimentacion_Pesaje/SectionViewer';
import Carnet from '../../components/Pet/Carnet';
import PetForm from '../../components/Pet/PetForm';
import { usePets } from '../../context/PetsContext';
import { hasExpiredCoverage, isDawerPet } from '../../utils/coverage';
import { useNewPetProfilePic, usePetActions, usePetPic } from '../Pet/usePetUtils';
import { useAlert } from '../UI/useAlert';
import { useModal } from '../UI/useModal';
import { useImageLoad } from '../Utils/useImageLoad';

const ICON_SIZE = 28;
const iconProps: IconProps = { size: ICON_SIZE, weight: 'fill' };

export const usePetPage = () => {
  const { push } = useIonRouter();
  const { currentPet, mutate } = usePets();
  const { id, nombre, raza } = currentPet || {};
  const isDawer = isDawerPet(currentPet);
  const isCoverageExpired = hasExpiredCoverage(currentPet);

  const { updatePetProfilePic, isLoading: isUpdatingProfilePic } = useNewPetProfilePic();
  const { petImage } = usePetPic(currentPet);
  const { handleImageLoad } = useImageLoad(petImage);
  const { isOpen, content, openModal, closeModal, modalProps } = useModal();
  const { alertState, showAlert, hideAlert } = useAlert();
  const {
    openNuevoSiniestro,
    openHistorial,
    openTelemedicina,
    openPlanPayment,
    openCoverageRequiredAlert,
  } = usePetActions({
    pet: currentPet,
    openModal,
    closeModal,
    showAlert,
  });

  const [showCameraOptions, setShowCameraOptions] = useState<boolean>(false);

  const goBack = useCallback(() => push('/tabs/home'), [push]);

  // Handlers de cámara
  const openCameraOptions = useCallback(() => {
    if (isCoverageExpired) {
      openCoverageRequiredAlert();
      return;
    }

    (document.activeElement as HTMLElement)?.blur();
    setShowCameraOptions(true);
  }, [isCoverageExpired, openCoverageRequiredAlert]);

  const closeCameraOptions = useCallback(() => setShowCameraOptions(false), []);

  const handleTakePhoto = useCallback(async () => {
    closeCameraOptions();
    if (id) await updatePetProfilePic(String(id), 'camera', mutate);
  }, [id, updatePetProfilePic, mutate, closeCameraOptions]);

  const handleSelectFromGallery = useCallback(async () => {
    closeCameraOptions();
    if (id) await updatePetProfilePic(String(id), 'gallery', mutate);
  }, [id, updatePetProfilePic, mutate, closeCameraOptions]);

  const openEditForm = () => {
    if (isCoverageExpired) {
      openCoverageRequiredAlert();
      return;
    }

    openModal(<PetForm pet={currentPet} closeModal={closeModal} />, {
      navigation: false,
    });
  };

  const petOptions = useMemo(() => {
    const baseOptions = [
      {
        onClick: openNuevoSiniestro,
        icon: <PlusIcon {...iconProps} />,
        title: 'Nuevo caso',
        description: isCoverageExpired
          ? 'Bloqueado por cobertura vencida'
          : isDawer
            ? 'Accidente o enfermedad'
            : 'Atención médica',
        disabled: isCoverageExpired,
      },
      {
        onClick: openHistorial,
        icon: <ClockCounterClockwiseIcon {...iconProps} />,
        title: 'Historial',
        description: isCoverageExpired
          ? 'Bloqueado por cobertura vencida'
          : 'Cuidados y reintegros',
        disabled: isCoverageExpired,
      },
      {
        onClick: openTelemedicina,
        icon: <PhonePlusIcon {...iconProps} />,
        title: 'Telemedicina',
        description: isCoverageExpired
          ? 'Bloqueado por cobertura vencida'
          : 'Guardería y eutanasia',
        disabled: isCoverageExpired,
      },
      {
        onClick: isCoverageExpired
          ? openCoverageRequiredAlert
          : () =>
              openModal(<Carnet pet={currentPet!} />, {
                navTitle: 'Carnet de asegurado',
              }),
        icon: <IdentificationCardIcon {...iconProps} />,
        title: 'Carnet',
        description: isCoverageExpired
          ? 'Bloqueado por cobertura vencida'
          : 'Datos del asegurado',
        disabled: isCoverageExpired,
      },
    ];

    const dawerPlanOption = {
      onClick: openPlanPayment,
      icon: <ShieldCheckIcon {...iconProps} />,
      title: 'Elegir Plan',
      description: 'Datos de pago',
      disabled: false,
    };

    const alimentacionOption = {
      onClick: () =>
        openModal(<AlimentacionPesaje pet={currentPet} closeModal={closeModal} />, {
          navigation: false,
        }),
      icon: <ForkKnifeIcon {...iconProps} />,
      title: 'Alimentación',
      description: 'Comidas y control de peso',
      disabled: false,
    };

    return [...baseOptions, isDawer ? dawerPlanOption : alimentacionOption];
  }, [
    currentPet,
    isDawer,
    isCoverageExpired,
    openModal,
    closeModal,
    openNuevoSiniestro,
    openHistorial,
    openTelemedicina,
    openPlanPayment,
    openCoverageRequiredAlert,
  ]);

  return {
    isDawer,
    isCoverageExpired,
    pet: {
      nombre,
      raza,
      image: petImage,
    },
    isUpdatingProfilePic,
    petOptions,
    modal: {
      isOpen,
      content,
      closeModal,
      ...modalProps,
    },
    alert: {
      ...alertState,
      hideAlert,
    },
    camera: {
      isOpen: showCameraOptions,
      open: openCameraOptions,
      close: closeCameraOptions,
      takePhoto: handleTakePhoto,
      selectFromGallery: handleSelectFromGallery,
    },
    goBack,
    handleImageLoad,
    openEditForm,
  };
};
